/**
 * The auth half-shell — `/login`, `/login/verify`, `/recover`.
 *
 * **Half the shell, and each dropped slot has its own reason rather than one
 * blanket one.** The avatar, the account menu and the notification handle all
 * lead to per-subscriber state behind the wall — they are not merely empty
 * before a session, they are unreachable. The navigation column and the bottom
 * bar carry the CATALOGUE destinations, and the catalogue is served on the
 * public host, one brand click away. The search-expand posts to that host too,
 * and these three screens are a single-task funnel. The breadcrumb has no
 * trail above it, because this area IS the entry.
 *
 * Drawing an account menu above a login form would offer «تسجيل الخروج» to a
 * reader who has not logged in — the same claim the authenticated top bar
 * avoids in the other direction when it refuses to draw a sign-in entry beside
 * an account avatar.
 *
 * **Two slots are kept, and neither is decoration.** The theme toggle is
 * `[x]` §18.2's display toggle and it writes the storage key all three
 * artifacts share, so a reader who chose light on the public site meets light
 * here rather than a flash of dark. The footer is kept because `[x]` §21.4
 * binds «every public AND gated surface» with no carve-out, and its two
 * informational destinations are among the few a pre-session reader can reach.
 *
 * **The brand lockup points at the PUBLIC host, not at this area's entry.**
 * `/` here is the subscriber home and needs a session; sending a reader who
 * has not got one there is sending them back to the wall they just came
 * through.
 *
 * **The bottom-bar reservation is cancelled by what the page contains.** The
 * app shell reserves space for the bottom bar at the base tier; these pages
 * carry none, so this shell simply does not reserve it — the page's own
 * content is what declares its kind.
 */
import { Outlet } from 'react-router-dom';

import { SITE_ORIGIN } from '@shared/api/config';
import { Footer, chromeCopy, footerInformationalEntries } from '@shared/chrome';
import { BrandLockup, IconThemeDark, IconThemeLight } from '@shared/chrome/icons';
import { useTheme } from '@shared/theme/ThemeProvider';

export function AuthShell() {
  const { theme, toggleTheme } = useTheme();
  const isLight = theme === 'light';

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-ink">
      {/* The first focusable element on every page. */}
      <a href="#main" className="skip-link">
        {chromeCopy.skipToContent}
      </a>

      <header
        role="banner"
        className="sticky top-0 z-50 flex items-center gap-3 border-b border-transparent px-gutter py-3"
      >
        <a
          href={SITE_ORIGIN}
          aria-label={chromeCopy.brandPublicSiteLabel}
          className="flex shrink-0 items-center gap-2 text-ink no-underline"
        >
          {/*
            The full lockup at every width. This is the pre-session half-shell:
            the brand, the theme toggle and the footer, and none of the signed-in
            controls that crowd the app's own top bar. The row that forced the
            seal-only reduction there does not exist here, so the word stays.
          */}
          <BrandLockup className="flex shrink-0 items-center gap-2 text-ink" label={chromeCopy.brand} />
        </a>

        <span className="ms-auto" />

        <button
          type="button"
          onClick={toggleTheme}
          aria-pressed={isLight}
          aria-label={isLight ? chromeCopy.topbar.toThemeDark : chromeCopy.topbar.toThemeLight}
          className="flex min-h-11 w-11 shrink-0 items-center justify-center rounded-sm p-0 text-ink transition-colors duration-fast hover:bg-hover-bg"
        >
          {isLight ? <IconThemeDark size={20} /> : <IconThemeLight size={20} />}
        </button>
      </header>

      {/*
        The column grows to fill what is between the bar and the footer and
        centres its card in it. A short form pinned under the top bar above a
        field of nothing reads as a half-loaded page.

        26rem at the base tier, widening to 30rem from `--bp-lg` — which is the
        cap the design system already declares for `.field` itself, so the
        column stops exactly where a wider one would start making the control
        wider than the system recommends.
      */}
      <main id="main" className="container grid flex-1 content-center justify-items-center py-7 md:py-9">
        <div data-auth="" className="w-full max-w-[26rem] lg:max-w-[30rem]">
          <Outlet />
        </div>
      </main>

      {/*
        The two informational entries only. Search and the editorial listing are
        catalogue destinations served on the public host, and this shell drops
        every catalogue destination — the nav column, the bottom bar and the
        search-expand went for that reason, so leaving two of them in the footer
        would put back through the bottom of the page what the top of it removed.
      */}
      <Footer entries={footerInformationalEntries} />
    </div>
  );
}
